import { ArrowRight } from "lucide-react";
import type { FengniaoDashboard } from "../hooks/useFengniaoDashboard";
import { formatBnb, formatToken } from "../lib/format";
import { StepCard, type StepCta, type StepStatus } from "./StepCard";

type ActionsShape = {
  phase: string;
  label: string;
  registerHolder: () => Promise<void> | void;
  claimHolderLp: () => Promise<void> | void;
  registerLpTime: () => Promise<void> | void;
  syncLpTime: () => Promise<void> | void;
  claimBnb: () => Promise<void> | void;
};

interface RewardFlowProps {
  dashboard: FengniaoDashboard;
  actions: ActionsShape;
}

interface StepDef {
  index: 0 | 1 | 2 | 3;
  title: string;
  status: StepStatus;
  hint?: string;
  cta?: StepCta;
}

function buildSteps(dashboard: FengniaoDashboard, actions: ActionsShape): StepDef[] {
  const { isConnected, stage, token, lp, holderLp, lpTime } = dashboard;
  const txActive = actions.phase !== "idle";

  // ---- Step 0: register holder ----
  const step0Status: StepStatus = holderLp.registered
    ? "done"
    : holderLp.canRegister
      ? "ready"
      : "locked";

  let step0Hint: string | undefined;
  if (!stage.tokenBound) step0Hint = "等待蜂鸟代币上线";
  else if (!isConnected) step0Hint = "请先连接钱包";
  else if (
    !holderLp.registered &&
    holderLp.minHolding > 0n &&
    token.walletBalance < holderLp.minHolding
  )
    step0Hint = `持币 ${formatToken(token.walletBalance, token.symbol, token.decimals)} / 需要 ${formatToken(holderLp.minHolding, token.symbol, token.decimals)}`;

  // ---- Step 1: claim LP ----
  const step1Status: StepStatus = holderLp.canClaim
    ? "ready"
    : holderLp.registered
      ? "active"
      : "locked";

  let step1Hint: string | undefined;
  if (!stage.lpBound) step1Hint = "等待 LP 池开盘";
  else if (holderLp.tokensSoldWarning) step1Hint = "你卖了部分持币，现在领奖励会归零";

  // ---- Step 2: register / sync LP-time ----
  const step2Status: StepStatus = lpTime.needsRegister
    ? "ready"
    : lpTime.needsSync
      ? "ready"
      : lpTime.registered
        ? "done"
        : "locked";

  let step2Hint: string | undefined;
  if (stage.lpTimePaused) step2Hint = "BNB 分红已暂停";
  else if (lpTime.decreased) step2Hint = "LP 比之前少了，同步会清空未领的 BNB";
  else if (!stage.lpBound) step2Hint = "等待 LP 创建";
  else if (
    !lpTime.registered &&
    lpTime.minLpHolding > 0n &&
    lp.walletBalance < lpTime.minLpHolding
  )
    step2Hint = `LP ${formatToken(lp.walletBalance, lp.symbol)} / 需要 ${formatToken(lpTime.minLpHolding, lp.symbol)}`;

  const step2Label = !lpTime.registered ? "开启 BNB 分红" : "同步新 LP";
  const step2OnClick = lpTime.needsRegister ? actions.registerLpTime : actions.syncLpTime;
  const step2Loading =
    txActive &&
    (actions.label === "开启 BNB 分红" || actions.label === "同步新 LP");

  // ---- Step 3: claim BNB ----
  const step3Status: StepStatus = lpTime.canClaim
    ? "ready"
    : lpTime.registered
      ? "active"
      : "locked";

  const step3Hint = stage.lpTimePaused ? "BNB 分红已暂停" : undefined;

  return [
    {
      index: 0,
      title: "开启持币分红",
      status: step0Status,
      hint: step0Hint,
      cta: {
        label: "注册",
        onClick: () => void actions.registerHolder(),
        disabled: !holderLp.canRegister,
        variant: "wing",
        loading: txActive && actions.label === "开启持币分红",
      },
    },
    {
      index: 1,
      title: "领取 LP",
      status: step1Status,
      hint: step1Hint,
      cta: {
        label: `领取 ${formatToken(holderLp.claimable, "LP")}`,
        onClick: () => void actions.claimHolderLp(),
        disabled: !holderLp.canClaim,
        variant: "wing",
        loading: txActive && actions.label === "领取 LP",
      },
    },
    {
      index: 2,
      title: "开启 / 同步 BNB 分红",
      status: step2Status,
      hint: step2Hint,
      cta: {
        label: step2Label,
        onClick: () => void step2OnClick(),
        disabled: !(lpTime.needsRegister || lpTime.needsSync) || stage.lpTimePaused,
        variant: "wing",
        loading: step2Loading,
      },
    },
    {
      index: 3,
      title: "领取 BNB",
      status: step3Status,
      hint: step3Hint,
      cta: {
        label: `领取 ${formatBnb(lpTime.claimableBnb)}`,
        onClick: () => void actions.claimBnb(),
        disabled: !lpTime.canClaim,
        variant: "bnb",
        loading: txActive && actions.label === "领取 BNB",
      },
    },
  ];
}

export function RewardFlow({ dashboard, actions }: RewardFlowProps) {
  const steps = buildSteps(dashboard, actions);

  return (
    <section aria-labelledby="reward-flow-title">
      <header className="mb-3 flex items-baseline justify-between gap-4">
        <h2
          id="reward-flow-title"
          className="text-sm font-mono uppercase tracking-wider text-ink"
        >
          奖励流程
        </h2>
        <p className="text-xs text-ink-dim">三步领完：先领 LP，让新 LP 计时，再领 BNB</p>
      </header>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-[1fr_auto_1fr_auto_1fr_auto_1fr] lg:items-center">
        <StepCard {...steps[0]} />
        <ArrowRight
          className="hidden lg:block w-5 h-5 text-ink-dim/40 mx-1"
          strokeWidth={1.75}
          aria-hidden
        />
        <StepCard {...steps[1]} />
        <ArrowRight
          className="hidden lg:block w-5 h-5 text-ink-dim/40 mx-1"
          strokeWidth={1.75}
          aria-hidden
        />
        <StepCard {...steps[2]} />
        <ArrowRight
          className="hidden lg:block w-5 h-5 text-ink-dim/40 mx-1"
          strokeWidth={1.75}
          aria-hidden
        />
        <StepCard {...steps[3]} />
      </div>
    </section>
  );
}
